// Spec 01 — three-slot header model.
// Maps an asset (+ its parent and property values keyed by code) onto Identity,
// Location and Volumetry slots per asset type. Empty slots return null so the
// renderer can omit them.

import { lotBaseTypeCode } from './asset-type-tree'

type AnyRecord = Record<string, any>

export interface AssetHeaderSlots {
    identity: { primary: string; external?: string } | null
    location: string | null
    volumetry: { value: string; label?: string } | null
}

const RESIDENTIAL_SUBTYPES = new Set(['residential', 'apartment', 'house', 'social_housing'])
const OFFICE_SUBTYPES = new Set(['office', 'tertiary'])
const RETAIL_SUBTYPES = new Set(['retail', 'shop', 'commercial'])
const SOCIAL_SUBTYPES = new Set(['social', 'social_housing'])

function fmtArea(v: any): string {
    const n = typeof v === 'number' ? v : Number(v)
    if (Number.isFinite(n)) return `${n.toLocaleString()} m²`
    return String(v)
}

/**
 * Pick the headline surface for a building, returning the canonical value and
 * its acronym label per spec 01 §1. Priority:
 *   1. living_area → SHAB (residential)
 *   2. usable_area → SUB (office) / SU (social) / GLA (retail)
 *   3. gross_floor_area → SDP
 *   4. surface_area → no acronym
 */
function pickHeadlineSurface(
    propsByCode: Record<string, any>,
    subtype?: string,
): AssetHeaderSlots['volumetry'] {
    const sub = (subtype || '').toLowerCase()

    const living = propsByCode.living_area
    if (living != null && living !== '' && RESIDENTIAL_SUBTYPES.has(sub)) {
        return { value: fmtArea(living), label: 'SHAB' }
    }

    const usable = propsByCode.usable_area
    if (usable != null && usable !== '') {
        let label = 'SUB'
        if (RETAIL_SUBTYPES.has(sub)) label = 'GLA'
        else if (SOCIAL_SUBTYPES.has(sub)) label = 'SU'
        else if (OFFICE_SUBTYPES.has(sub)) label = 'SUB'
        return { value: fmtArea(usable), label }
    }

    const gfa = propsByCode.gross_floor_area
    if (gfa != null && gfa !== '') return { value: fmtArea(gfa), label: 'SDP' }

    const sa = propsByCode.surface_area
    if (sa != null && sa !== '') return { value: fmtArea(sa) }

    return null
}

function pickGenericSurface(propsByCode: Record<string, any>): AssetHeaderSlots['volumetry'] {
    for (const code of ['surface_area', 'usable_area', 'living_area', 'gross_floor_area']) {
        const v = propsByCode[code]
        if (v != null && v !== '') return { value: fmtArea(v) }
    }
    return null
}

interface BuildSlotsArgs {
    asset: AnyRecord
    assetTypeCode?: string
    parentAsset?: AnyRecord | null
    parentChain?: AnyRecord[]
    propsByCode?: Record<string, any>
    addressShort?: string | null
}

export function getAssetHeaderSlots({
    asset,
    assetTypeCode,
    parentAsset,
    parentChain,
    propsByCode = {},
    addressShort,
}: BuildSlotsArgs): AssetHeaderSlots {
    const externalCode: string | undefined = asset?.external_ids?.code || undefined
    const code = (assetTypeCode || '').toLowerCase()

    const identityFor = (primary: string): AssetHeaderSlots['identity'] => ({
        primary,
        external: externalCode,
    })

    const baseName: string = asset?.name || asset?.code || asset?.uid || ''

    const upChain = (parentChain || [])
        .filter((p) => p?.uid && p.uid !== asset?.uid)
        .sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0))
        .map((p) => p.name || p.code || '')
        .filter(Boolean)
    const parentSummary = upChain.length ? upChain.join(' · ') : (parentAsset?.name || null)

    // Note on addresses: only `building` puts one in the location slot, and the
    // renderer drops that slot for buildings precisely because the address box
    // below already shows it. Every other address-bearing type (`parcel_land`,
    // `house` — both `with_address: true` — and `building_group`, which renders
    // SiteAddressDisplay) gets its address from that box too, so repeating it
    // here would duplicate it on screen. They are situated by their ancestry
    // instead. A dead `case 'land_parcel'` used to sit here — the ontology code
    // is `parcel_land`, so it never matched; parcels have always fallen through
    // to `default`, which is the behaviour we want.
    switch (lotBaseTypeCode(code)) {
        // A site is situated by its ancestry and shows no headline surface.
        case 'building_group':
            return {
                identity: identityFor(baseName),
                location: parentSummary,
                volumetry: null,
            }
        case 'building':
            return {
                identity: identityFor(baseName),
                location: addressShort || null,
                volumetry: pickHeadlineSurface(propsByCode, asset?.subtype),
            }
        case 'floor':
            return {
                identity: identityFor(baseName),
                location: parentSummary,
                volumetry: pickGenericSurface(propsByCode),
            }
        case 'unit': {
            const usable = propsByCode.usable_area
            return {
                identity: identityFor(baseName),
                location: parentSummary,
                volumetry: usable != null && usable !== ''
                    ? { value: fmtArea(usable) }
                    : pickGenericSurface(propsByCode),
            }
        }
        case 'room':
            return {
                identity: identityFor(baseName),
                location: parentSummary,
                volumetry: pickGenericSurface(propsByCode),
            }
        case 'equipment': {
            const brand = propsByCode.equipment_brand
            const model = propsByCode.equipment_model
            const parts = [brand, model].filter(Boolean) as string[]
            return {
                identity: identityFor(parts.length ? parts.join(' ') : baseName),
                location: parentSummary,
                volumetry: null,
            }
        }
        default:
            return {
                identity: identityFor(baseName),
                location: parentSummary,
                volumetry: pickGenericSurface(propsByCode),
            }
    }
}
